import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Save, X, Trash2, FileCheck2, Upload, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH = { withCredentials: true };
const EMPTY = { lot: "", compound: "", identity: "See batch record", purity: "See batch record", date: "", coa_url: "" };

export default function AdminBatches() {
  const auth = AUTH;
  const [batches, setBatches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/batches`, AUTH);
      setBatches(data);
    } catch (e) { toast.error("Could not load batches"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setEditing((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!editing.lot.trim()) { toast.error("Batch / lot number is required"); return; }
    if (!editing.compound.trim()) { toast.error("Compound is required"); return; }
    if (editing._isNew) {
      const dupe = batches.some((b) => b.lot === editing.lot.trim().toUpperCase());
      if (dupe && !window.confirm(`Batch ${editing.lot.toUpperCase()} already exists. Saving will overwrite that record. Continue?`)) return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/batches`, editing, auth);
      toast.success(`Saved batch ${editing.lot.toUpperCase()}`);
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save batch"); }
    finally { setSaving(false); }
  };

  const remove = async (lot) => {
    if (!window.confirm(`Delete batch ${lot}? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/admin/batches/${lot}`, auth); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const uploadCoa = async (lot, file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) { toast.error("COA must be a PDF"); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/admin/batches/${lot}/coa`, fd, { ...AUTH, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Batch COA uploaded"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "COA upload failed"); }
  };

  const removeCoa = async (lot) => {
    try { await axios.delete(`${API}/admin/batches/${lot}/coa`, auth); toast.success("COA removed"); load(); }
    catch (e) { toast.error("Could not remove COA"); }
  };

  // One-step quick upload: drop a PDF -> confirm lot/compound -> record created + COA attached.
  const [drop, setDrop] = useState(null); // { file, lot, compound, purity, date }
  const [dragOver, setDragOver] = useState(false);
  const [dropBusy, setDropBusy] = useState(false);

  const startDrop = (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) { toast.error("COA must be a PDF file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("COA file too large (max 10MB)"); return; }
    const guess = file.name.replace(/\.pdf$/i, "").replace(/[^a-zA-Z0-9-]+/g, "-").replace(/^-+|-+$/g, "").toUpperCase().slice(0, 24);
    setDrop({ file, lot: guess, compound: "", purity: "See attached COA", date: new Date().toISOString().slice(0, 10) });
  };

  const submitDrop = async () => {
    if (!drop.lot.trim()) { toast.error("Batch / lot number is required"); return; }
    if (!drop.compound.trim()) { toast.error("Compound name is required"); return; }
    const key = drop.lot.trim().toUpperCase();
    if (batches.some((b) => b.lot === key) &&
        !window.confirm(`Batch ${key} already exists. Uploading will replace its record and COA. Continue?`)) return;
    setDropBusy(true);
    try {
      await axios.post(`${API}/admin/batches`, {
        lot: drop.lot.trim().toUpperCase(), compound: drop.compound.trim(),
        identity: "See attached COA", purity: drop.purity || "See attached COA",
        date: drop.date || "", coa_url: "",
      }, auth);
      const fd = new FormData(); fd.append("file", drop.file);
      await axios.post(`${API}/admin/batches/${drop.lot.trim().toUpperCase()}/coa`, fd,
        { ...AUTH, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Batch ${drop.lot.trim().toUpperCase()} created with COA attached`);
      setDrop(null); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed — please try again");
    } finally { setDropBusy(false); }
  };

  if (editing) {
    return (
      <div className="border border-zinc-800 bg-[#0C0C0C] p-6" data-testid="admin-batch-editor">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl font-light">{editing._isNew ? "New batch record" : `Editing: ${editing.lot}`}</h3>
          <button onClick={() => setEditing(null)} data-testid="batch-cancel" className="text-[#71717A] hover:text-white"><X size={20} /></button>
        </div>
        <p className="text-[#71717A] text-sm mb-6 max-w-xl leading-relaxed">Records entered here power the public <span className="text-[#D4AF37]">Verify a Batch</span> lookup. Only enter documentation you actually have — never fabricate identity or purity values.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <BField label="Batch / Lot number" value={editing.lot} onChange={(v) => setField("lot", v)} disabled={!editing._isNew} testid="batch-lot" />
          <BField label="Compound" value={editing.compound} onChange={(v) => setField("compound", v)} testid="batch-compound" />
          <BField label="Identity" value={editing.identity} onChange={(v) => setField("identity", v)} testid="batch-identity" />
          <BField label="Purity" value={editing.purity} onChange={(v) => setField("purity", v)} testid="batch-purity" />
          <BField label="Date (e.g. 2026-07-01)" value={editing.date} onChange={(v) => setField("date", v)} testid="batch-date" />
          <BField label="External COA URL (optional)" value={editing.coa_url} onChange={(v) => setField("coa_url", v)} testid="batch-coa-url" />
        </div>
        <button onClick={save} disabled={saving} data-testid="batch-save"
          className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] text-black px-7 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors disabled:opacity-50">
          <Save size={14} /> {saving ? "Saving…" : "Save batch"}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-batches">
      {/* Quick COA upload — create a batch record straight from a PDF */}
      {drop ? (
        <div className="border border-[#D4AF37]/50 bg-[#D4AF37]/[0.05] p-5 mb-5" data-testid="coa-drop-form">
          <div className="flex items-center justify-between mb-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Attach COA: {drop.file.name} ({Math.round(drop.file.size / 1024)} KB)</p>
            <button onClick={() => setDrop(null)} data-testid="coa-drop-cancel" className="text-[#71717A] hover:text-white"><X size={16} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <BField label="Batch / Lot number" value={drop.lot} onChange={(v) => setDrop((p) => ({ ...p, lot: v }))} testid="coa-drop-lot" />
            <BField label="Compound" value={drop.compound} onChange={(v) => setDrop((p) => ({ ...p, compound: v }))} testid="coa-drop-compound" />
            <BField label="Purity (optional)" value={drop.purity} onChange={(v) => setDrop((p) => ({ ...p, purity: v }))} testid="coa-drop-purity" />
            <BField label="Date" value={drop.date} onChange={(v) => setDrop((p) => ({ ...p, date: v }))} testid="coa-drop-date" />
          </div>
          <button onClick={submitDrop} disabled={dropBusy} data-testid="coa-drop-submit"
            className="mt-4 w-full sm:w-auto min-h-[48px] inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-7 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors disabled:opacity-50">
            <Upload size={14} /> {dropBusy ? "Uploading…" : "Create Batch & Attach COA"}
          </button>
        </div>
      ) : (
        <label data-testid="coa-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); startDrop(e.dataTransfer.files?.[0]); }}
          className={`flex flex-col items-center justify-center gap-2 border border-dashed p-6 mb-5 cursor-pointer transition-colors ${dragOver ? "border-[#D4AF37] bg-[#D4AF37]/[0.08]" : "border-zinc-700 bg-[#0C0C0C] hover:border-[#D4AF37]/60"}`}>
          <Upload size={20} className="text-[#D4AF37]" />
          <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#F4F4F5]">Drop a COA PDF here or tap to browse</p>
          <p className="font-mono text-[9px] text-[#52525B]">Creates the batch record and attaches the file in one step · PDF up to 10MB</p>
          <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="coa-dropzone-input"
            onChange={(e) => { startDrop(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      )}
      <div className="flex items-center justify-between mb-5">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#52525B]">{batches.length} batch record{batches.length === 1 ? "" : "s"}</p>
        <button onClick={() => setEditing({ ...EMPTY, _isNew: true })} data-testid="batch-new-btn" className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-[#C5A028] transition-colors">
          <Plus size={13} /> New batch
        </button>
      </div>
      {batches.length === 0 ? (
        <div className="border border-zinc-800 bg-[#0C0C0C] p-8 text-center" data-testid="batches-empty">
          <p className="text-[#A1A1AA] text-sm mb-1">No batch records yet.</p>
          <p className="text-[#71717A] text-sm">Add a batch so researchers can verify it on the public lookup page.</p>
        </div>
      ) : (
        <div className="border border-zinc-800 divide-y divide-zinc-800">
          {batches.map((b) => (
            <div key={b.lot} className="flex flex-wrap items-center justify-between gap-4 p-4" data-testid={`batch-row-${b.lot}`}>
              <div className="min-w-0">
                <p className="font-serif text-lg truncate">{b.compound}
                  {b.coa_download && <FileCheck2 size={14} className="inline ml-2 text-emerald-400" />}
                </p>
                <p className="font-mono text-[10px] text-[#71717A] mt-0.5">{b.lot} · {b.purity || "—"} · {b.date || "—"}</p>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0 items-center">
                <label className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#A1A1AA] hover:text-[#D4AF37] transition-colors cursor-pointer inline-flex items-center gap-1.5 border border-zinc-700 px-3 py-2" data-testid={`batch-coa-upload-${b.lot}`}>
                  <Upload size={12} /> {b.coa_download ? "Replace COA" : "Upload COA"}
                  <input type="file" accept="application/pdf" className="hidden" onChange={(e) => { uploadCoa(b.lot, e.target.files[0]); e.target.value = ""; }} />
                </label>
                {b.coa_download && <a href={`${API}/batches/${b.lot}/coa`} target="_blank" rel="noreferrer" data-testid={`batch-coa-view-${b.lot}`} className="font-mono text-[9px] uppercase tracking-[0.15em] text-emerald-400/80 hover:text-emerald-400 transition-colors inline-flex items-center gap-1"><ExternalLink size={11} /> View</a>}
                {b.coa_download && <button onClick={() => removeCoa(b.lot)} data-testid={`batch-coa-remove-${b.lot}`} className="inline-flex items-center border border-zinc-700 px-3 py-2 font-mono text-[9px] uppercase tracking-[0.15em] text-[#71717A] hover:border-red-500 hover:text-red-400 transition-colors">Remove COA</button>}
                <button onClick={() => setEditing({ ...b })} data-testid={`batch-edit-${b.lot}`} className="flex items-center gap-1.5 border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">Edit</button>
                <button onClick={() => remove(b.lot)} data-testid={`batch-delete-${b.lot}`} className="flex items-center border border-zinc-700 px-3 py-2 text-[#71717A] hover:border-red-500 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BField({ label, value, onChange, disabled, testid }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid}
        className="w-full bg-[#0A0A0A] border border-zinc-800 focus:border-[#D4AF37] outline-none px-3 py-2.5 font-mono text-sm text-white disabled:opacity-50" />
    </div>
  );
}
